import * as React from "react";
import { cn } from "../lib/cn";

/**
 * Separator — thin divider between content blocks.
 *
 * Plain `<div role="separator">` with token-driven background. Default is
 * decorative (role="none") so screen readers don't announce a meaningless
 * boundary; pass `decorative={false}` for genuinely structural splits
 * (e.g. between a header section and a content section that constitute
 * different landmarks for assistive tech).
 *
 *   <Separator />                              // horizontal, decorative
 *   <Separator orientation="vertical" />       // vertical
 *   <Separator decorative={false} />           // semantic separator
 *
 * No Radix dep — the shadcn version of this primitive wraps
 * `@radix-ui/react-separator` for the decorative-vs-semantic role
 * switch, but the same role/aria wiring is two lines of pure HTML.
 */
export interface SeparatorProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual orientation. Default `"horizontal"`. */
  orientation?: "horizontal" | "vertical";
  /**
   * When true (default), renders `role="none"` so screen readers ignore
   * the divider. Set false for semantic separations (e.g. between content
   * landmarks).
   */
  decorative?: boolean;
}

export const Separator = React.forwardRef<HTMLDivElement, SeparatorProps>(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => (
    <div
      ref={ref}
      role={decorative ? "none" : "separator"}
      aria-orientation={!decorative ? orientation : undefined}
      className={cn(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className,
      )}
      {...props}
    />
  ),
);
Separator.displayName = "Separator";
